import React from "react";
import { Plus, Trash2 } from "lucide-react";

export function Field({ label, hint, children, required }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[0.7rem] uppercase tracking-[0.18em] font-semibold text-[#0a0a0a]">
          {label}
          {required && <span className="text-[#c40000] ml-1">*</span>}
        </span>
      </div>
      {children}
      {hint && <div className="mt-1 text-[0.7rem] text-[#525252]">{hint}</div>}
    </label>
  );
}

export function TextInput({ value, onChange, placeholder, ...rest }) {
  return (
    <input
      type="text"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-white border border-black/15 px-3 py-2.5 text-sm focus:outline-none focus:border-[#c40000] transition-colors"
      {...rest}
    />
  );
}

export function NumberInput({ value, onChange, placeholder, step, ...rest }) {
  return (
    <input
      type="number"
      step={step ?? "any"}
      value={value === null || value === undefined ? "" : value}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === "" ? null : Number(v));
      }}
      placeholder={placeholder}
      className="w-full bg-white border border-black/15 px-3 py-2.5 text-sm focus:outline-none focus:border-[#c40000] transition-colors"
      {...rest}
    />
  );
}

export function TextArea({ value, onChange, rows = 3, ...rest }) {
  return (
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="w-full bg-white border border-black/15 px-3 py-2.5 text-sm focus:outline-none focus:border-[#c40000] transition-colors resize-y leading-relaxed"
      {...rest}
    />
  );
}

/** Editable list of strings (e.g., modules, includes). */
export function StringListEditor({ items = [], onChange, placeholder, addLabel = "Agregar" }) {
  const update = (i, v) => {
    const next = [...items];
    next[i] = v;
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);

  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="text"
            value={it}
            onChange={(e) => update(i, e.target.value)}
            placeholder={placeholder}
            className="flex-1 bg-white border border-black/15 px-3 py-2 text-sm focus:outline-none focus:border-[#c40000] transition-colors"
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="w-9 h-9 flex items-center justify-center border border-black/15 text-[#525252] hover:bg-[#c40000] hover:text-white hover:border-[#c40000] transition-colors"
            aria-label="Eliminar"
          >
            <Trash2 size={13} strokeWidth={1.5} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.18em] font-semibold px-3 py-2 border border-black/15 hover:bg-[#0a0a0a] hover:text-white transition-colors"
      >
        <Plus size={13} strokeWidth={1.6} /> {addLabel}
      </button>
    </div>
  );
}

/** Editable list of schedule groups: [{label, slots: []}, ...] */
export function SchedulesEditor({ items = [], onChange }) {
  const updateGroup = (i, patch) => {
    const next = items.map((g, idx) => (idx === i ? { ...g, ...patch } : g));
    onChange(next);
  };
  const removeGroup = (i) => onChange(items.filter((_, idx) => idx !== i));
  const addGroup = () => onChange([...items, { label: "", slots: [""] }]);

  return (
    <div className="space-y-4">
      {items.map((g, i) => (
        <div key={i} className="border border-black/10 p-3 space-y-3 bg-[#fafafa]">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={g.label || ""}
              onChange={(e) => updateGroup(i, { label: e.target.value })}
              placeholder="Etiqueta (ej. Entre semana)"
              className="flex-1 bg-white border border-black/15 px-3 py-2 text-sm focus:outline-none focus:border-[#c40000] transition-colors"
            />
            <button
              type="button"
              onClick={() => removeGroup(i)}
              className="w-9 h-9 flex items-center justify-center border border-black/15 text-[#525252] hover:bg-[#c40000] hover:text-white hover:border-[#c40000] transition-colors"
              aria-label="Eliminar grupo"
            >
              <Trash2 size={13} strokeWidth={1.5} />
            </button>
          </div>
          <StringListEditor
            items={g.slots || []}
            onChange={(slots) => updateGroup(i, { slots })}
            placeholder="Turno (ej. 9:00 - 14:00)"
            addLabel="Agregar turno"
          />
        </div>
      ))}
      <button
        type="button"
        onClick={addGroup}
        className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.18em] font-semibold px-3 py-2 border border-black/15 hover:bg-[#0a0a0a] hover:text-white transition-colors"
      >
        <Plus size={13} strokeWidth={1.6} /> Agregar grupo de horarios
      </button>
    </div>
  );
}

/**
 * Prices by branch. Value per branch can be: number | "consultar" | "no_disponible".
 */
export function PricesEditor({ branches = [], value = {}, onChange }) {
  const setBranch = (id, val) => onChange({ ...value, [id]: val });

  return (
    <div className="space-y-3" data-testid="prices-editor">
      {branches.map((b) => {
        const raw = value[b.id];
        const mode =
          raw === "consultar"
            ? "consultar"
            : raw === "no_disponible"
            ? "no_disponible"
            : typeof raw === "number"
            ? "numero"
            : "vacio";
        return (
          <div key={b.id} className="flex flex-wrap items-center gap-3 border border-black/10 p-3 bg-[#fafafa]">
            <div className="flex-1 min-w-[140px]">
              <div className="font-semibold text-sm text-[#0a0a0a]">{b.name}</div>
              <div className="text-[0.65rem] uppercase tracking-[0.18em] text-[#525252] mt-0.5">
                id: {b.id}
              </div>
            </div>
            <select
              value={mode}
              onChange={(e) => {
                const m = e.target.value;
                if (m === "numero") setBranch(b.id, typeof raw === "number" ? raw : 200);
                else if (m === "consultar") setBranch(b.id, "consultar");
                else if (m === "no_disponible") setBranch(b.id, "no_disponible");
                else {
                  const next = { ...value };
                  delete next[b.id];
                  onChange(next);
                }
              }}
              className="bg-white border border-black/15 px-3 py-2 text-sm focus:outline-none focus:border-[#c40000]"
            >
              <option value="vacio">— No definido —</option>
              <option value="numero">Precio semanal ($MXN)</option>
              <option value="consultar">Consultar</option>
              <option value="no_disponible">No disponible</option>
            </select>
            {mode === "numero" && (
              <input
                type="number"
                value={typeof raw === "number" ? raw : 0}
                onChange={(e) => setBranch(b.id, Number(e.target.value || 0))}
                className="w-28 bg-white border border-black/15 px-3 py-2 text-sm focus:outline-none focus:border-[#c40000]"
              />
            )}
          </div>
        );
      })}
      {branches.length === 0 && (
        <div className="text-[0.7rem] text-[#525252]">
          Crea sucursales primero para definir precios por sucursal.
        </div>
      )}
    </div>
  );
}
